import {
  Theme,
  alpha,
  createTheme,
  darken,
  getContrastRatio,
} from "@mui/material/styles";
import { AffiliateTheme } from "./types";

export type BrandColors = Pick<AffiliateTheme, "primary" | "background" | "text">;

/** Black or white, whichever contrasts more. The admin API applies the same rule when validating. */
export function readableTextOn(background: string): string {
  return getContrastRatio(background, "#FFFFFF") >= getContrastRatio(background, "#000000")
    ? "#FFFFFF"
    : "#000000";
}

/**
 * The brand colour darkened until it is readable as text on the page, for the places that tint
 * text and icons rather than filling a shape. A brand colour only has to reach 3:1 against the
 * background to be published, which is fine behind white text on a button but too weak for
 * body text, so it cannot be used as-is.
 */
function readableAccentOn(color: string, surface: string): string {
  let accent = color;

  for (let step = 0; step < 12 && getContrastRatio(accent, surface) < 4.5; step += 1) {
    accent = darken(accent, 0.1);
  }

  return accent;
}

export interface AffiliateThemeOptions {
  /**
   * "full" also applies the affiliate's background and text colours (the sign-in page).
   * "accents" keeps the product's backgrounds and text, changing only the primary colour, so
   * dashboards, grids and charts keep their tested contrast.
   */
  scope?: "full" | "accents";
}

/** Derives a MUI theme from affiliate colours. */
export function createAffiliateTheme(
  base: Theme,
  colors: BrandColors,
  options: AffiliateThemeOptions = {}
): Theme {
  const primary = base.palette.augmentColor({
    color: { main: colors.primary, contrastText: readableTextOn(colors.primary) },
  });

  const fullPalette = options.scope !== "accents";
  const surface = fullPalette ? colors.background : base.palette.background.paper;

  // primary.dark is what the app uses wherever the brand tints text or an icon.
  primary.dark = readableAccentOn(colors.primary, surface);

  // On the sign-in page the text colour is validated at 4.5:1 against the affiliate background, so it
  // makes a visible focus ring. Elsewhere the product's own text colour does the same job.
  const focusRing = {
    outline: `3px solid ${fullPalette ? colors.text : base.palette.text.primary}`,
    outlineOffset: 2,
  };

  return createTheme(base, {
    palette: {
      primary,
      ...(fullPalette
        ? {
            background: { default: colors.background, paper: colors.background },
            text: { primary: colors.text, secondary: alpha(colors.text, 0.8) },
            divider: alpha(colors.text, 0.2),
          }
        : {}),
    },
    components: {
      MuiButtonBase: {
        styleOverrides: {
          root: { "&.Mui-focusVisible": focusRing },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: { "&:focus-visible": { ...focusRing, borderRadius: 2 } },
        },
      },
    },
  });
}
